import { getIntegrationsApi } from '@/api-calls/integrations/get'
import {
  deleteIntegrationApi,
  upsertIntegrationApi,
} from '@/api-calls/integrations/mutate'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import { googleAnalyticsConfigSchema, openpanelConfigSchema } from '@/lib/schema/integrations'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Badge,
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Skeleton,
  Switch,
} from '@blogging/ui'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import { BarChart3, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

interface Integration {
  id: string
  siteId: string
  provider: string
  enabled: boolean
  openpanelConfig?: {
    clientId: string
    trackScreenViews: boolean
    trackOutgoingLinks: boolean
  } | null
  googleAnalyticsConfig?: {
    measurementId: string
  } | null
}

const PROVIDERS = [
  {
    id: 'google_analytics',
    name: 'Google Analytics 4',
    description: 'Track visitors and measure engagement with GA4.',
    url: 'https://analytics.google.com',
    icon: BarChart3,
  },
  {
    id: 'openpanel',
    name: 'OpenPanel',
    description: 'Open-source analytics for your site.',
    url: 'https://openpanel.dev',
    icon: BarChart3,
  },
] as const

export function SiteSettingsIntegrationsPage() {
  const { siteId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null)

  const {
    data: integrationsData,
    isLoading,
    error,
  } = useFetch(getIntegrationsApi, {
    resourceName: 'integrations',
    initialParams: { siteId: siteId as string },
  })

  const integrations: Integration[] = integrationsData ?? []

  const invalidateIntegrations = () => {
    queryClient.invalidateQueries({
      queryKey: [getIntegrationsApi.apiName, { siteId }],
    })
  }

  const { mutate: upsertIntegration, isPending: isUpserting } = useMutation(
    upsertIntegrationApi,
    {
      resourceName: 'integration',
      onSuccess: () => {
        invalidateIntegrations()
        toast.success('Integration updated!')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to update integration')
      },
    },
  )

  const { mutate: deleteIntegration } = useMutation(deleteIntegrationApi, {
    resourceName: 'integration',
    onSuccess: () => {
      invalidateIntegrations()
      toast.success('Integration removed!')
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to remove integration')
    },
  })

  const handleToggle = (provider: string, currentlyEnabled: boolean) => {
    if (currentlyEnabled) {
      deleteIntegration({ siteId: siteId as string, provider })
      setExpandedProvider(null)
    } else if (expandedProvider === provider) {
      // Pending enable — user toggled back off before saving
      setExpandedProvider(null)
    } else {
      setExpandedProvider(provider)
    }
  }

  if (isLoading) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <Skeleton className="h-48 w-full rounded-lg" />
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center">
        <p className="text-destructive">Error loading integrations</p>
        <Button variant="outline" onClick={() => navigate(0)} className="mt-2">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-8">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">Integrations</h1>
            <Badge variant="secondary">Beta</Badge>
          </div>
          <p className="text-muted-foreground">
            Connect third-party services to your site.
          </p>
        </div>

        <div className="space-y-4">
          {PROVIDERS.map((provider) => {
            const existing = integrations.find(
              (i) => i.provider === provider.id,
            )
            const isEnabled = existing?.enabled ?? false
            const isExpanded = expandedProvider === provider.id
            const isVisuallyEnabled = isEnabled || isExpanded

            return (
              <div key={provider.id} className="rounded-lg border bg-card">
                <div className="flex items-center justify-between p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                      <provider.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{provider.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {provider.description}{' '}
                        <a
                          href={provider.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-foreground"
                        >
                          Learn more
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isVisuallyEnabled && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setExpandedProvider(isExpanded ? null : provider.id)
                        }
                      >
                        Configure
                        {isExpanded ? (
                          <ChevronDown className="ml-1 h-4 w-4" />
                        ) : (
                          <ChevronRight className="ml-1 h-4 w-4" />
                        )}
                      </Button>
                    )}
                    <Switch
                      checked={isVisuallyEnabled}
                      onCheckedChange={() =>
                        handleToggle(provider.id, isEnabled)
                      }
                    />
                  </div>
                </div>

                {isExpanded && provider.id === 'google_analytics' && (
                  <div className="border-t px-6 pb-6 pt-4">
                    <GoogleAnalyticsConfigForm
                      existing={existing}
                      isUpserting={isUpserting}
                      onSubmit={(config) => {
                        upsertIntegration({
                          siteId: siteId as string,
                          provider: 'google_analytics',
                          enabled: true,
                          config,
                        })
                      }}
                    />
                  </div>
                )}

                {isExpanded && provider.id === 'openpanel' && (
                  <div className="border-t px-6 pb-6 pt-4">
                    <OpenpanelConfigForm
                      siteId={siteId as string}
                      existing={existing}
                      isUpserting={isUpserting}
                      onSubmit={(config) => {
                        upsertIntegration({
                          siteId: siteId as string,
                          provider: 'openpanel',
                          enabled: true,
                          config,
                        })
                      }}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}

function GoogleAnalyticsConfigForm({
  existing,
  isUpserting,
  onSubmit,
}: {
  existing?: Integration
  isUpserting: boolean
  onSubmit: (config: { measurementId: string }) => void
}) {
  const form = useForm({
    resolver: zodResolver(googleAnalyticsConfigSchema),
    defaultValues: {
      measurementId: existing?.googleAnalyticsConfig?.measurementId ?? '',
    },
  })

  useEffect(() => {
    form.reset({
      measurementId: existing?.googleAnalyticsConfig?.measurementId ?? '',
    })
  }, [existing?.googleAnalyticsConfig?.measurementId])

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => onSubmit(data))}
        className="space-y-6"
      >
        <FormField
          control={form.control}
          name="measurementId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Measurement ID</FormLabel>
              <FormControl>
                <Input
                  placeholder="G-XXXXXXXXXX"
                  {...field}
                  className="max-w-md"
                />
              </FormControl>
              <FormDescription>
                Find your Measurement ID in Google Analytics → Admin → Data Streams.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isUpserting}>
          {isUpserting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isUpserting ? 'Saving...' : 'Save Configuration'}
        </Button>
      </form>
    </Form>
  )
}

function OpenpanelConfigForm({
  siteId,
  existing,
  isUpserting,
  onSubmit,
}: {
  siteId: string
  existing?: Integration
  isUpserting: boolean
  onSubmit: (config: {
    clientId: string
    trackScreenViews: boolean
    trackOutgoingLinks: boolean
  }) => void
}) {
  const form = useForm({
    resolver: zodResolver(openpanelConfigSchema),
    defaultValues: {
      clientId: existing?.openpanelConfig?.clientId ?? '',
      trackScreenViews: existing?.openpanelConfig?.trackScreenViews ?? true,
      trackOutgoingLinks: existing?.openpanelConfig?.trackOutgoingLinks ?? true,
    },
  })

  useEffect(() => {
    form.reset({
      clientId: existing?.openpanelConfig?.clientId ?? '',
      trackScreenViews: existing?.openpanelConfig?.trackScreenViews ?? true,
      trackOutgoingLinks: existing?.openpanelConfig?.trackOutgoingLinks ?? true,
    })
  }, [existing?.openpanelConfig?.clientId, existing?.openpanelConfig?.trackScreenViews, existing?.openpanelConfig?.trackOutgoingLinks])

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => onSubmit(data))}
        className="space-y-6"
      >
        <FormField
          control={form.control}
          name="clientId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Client ID</FormLabel>
              <FormControl>
                <Input
                  placeholder="Your OpenPanel Client ID"
                  {...field}
                  className="max-w-md"
                />
              </FormControl>
              <FormDescription>
                Find your Client ID in your OpenPanel project settings.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="trackScreenViews"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Track screen views</FormLabel>
                <FormDescription>
                  Automatically track page views on your site.
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="trackOutgoingLinks"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  Track outgoing links
                </FormLabel>
                <FormDescription>
                  Track when visitors click links that leave your site.
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isUpserting}>
          {isUpserting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isUpserting ? 'Saving...' : 'Save Configuration'}
        </Button>
      </form>
    </Form>
  )
}
